import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatDateLong } from 'lib/dateFormat';
import { StatusBadge, scoreTextClass } from 'components/ui/status-badge';
import type * as ResultTypes from 'modules/result/types';

type Result = ResultTypes.IEntity.Result;

/**
 * A result's headline figure: the percentage in its score band, or for IELTS/SAT
 * the band or scaled total in ink. Attempts that are not finished show a dash.
 */
export function ResultScore({
  result,
  className,
}: {
  result: Pick<Result, 'status' | 'percentage' | 'examScore'>;
  className?: string;
}) {
  const { t } = useTranslation();
  if (result.status !== 'completed') {
    return <span className={cn('text-muted-foreground/70', className)}>—</span>;
  }
  const exam = result.examScore;
  if (exam) {
    const pending = exam.value === null;
    const text = exam.value === null ? '…' : exam.standard === 'IELTS' ? exam.value.toFixed(1) : String(exam.value);
    const label =
      exam.value === null
        ? t('exam.report.overall_pending')
        : exam.standard === 'IELTS'
          ? t('result.band_value', { band: exam.value.toFixed(1) })
          : t('result.sat_value', { score: exam.value });
    return (
      <span className={cn('tabular-nums', pending ? 'text-muted-foreground' : 'text-foreground', className)}>
        <span aria-hidden="true">{text}</span>
        <span className="sr-only">{label}</span>
      </span>
    );
  }
  const percent = Math.round(result.percentage);
  return <span className={cn('tabular-nums', scoreTextClass(percent), className)}>{percent}%</span>;
}

const hasVerdict = (result: Result) => result.status !== 'completed' || result.passed != null;

/** Pass/fail only once an attempt is finished; IELTS/SAT and marketplace results carry no verdict. */
function ResultVerdict({ result }: { result: Result }) {
  const { t } = useTranslation();
  if (result.status === 'in_progress') {
    return <StatusBadge tone="warning">{t('result.status_in_progress')}</StatusBadge>;
  }
  if (result.status === 'abandoned') {
    return <StatusBadge tone="neutral">{t('result.status_abandoned')}</StatusBadge>;
  }
  if (result.passed == null) return null;
  return (
    <StatusBadge tone={result.passed ? 'success' : 'error'}>
      {result.passed ? t('result.passed') : t('result.failed')}
    </StatusBadge>
  );
}

interface ResultRowProps {
  result: Result;
  /** Desktop: the row selects the report beside the list. Phones pass nothing and the row opens the result. */
  selected?: boolean;
  onSelect?: () => void;
}

/** One divided 64px row: score, title and date, verdict, then "Ochish" (or a chevron on phones). Unfinished attempts are shown, not opened. */
export function ResultRow({ result, selected = false, onSelect }: ResultRowProps) {
  const { t } = useTranslation();
  // An unfinished attempt has no completion date; its badge carries the status, so show when it began.
  const date = formatDateLong(result.completedAt ?? result.startedAt);
  const body = (
    <>
      <ResultScore result={result} className="row-span-2 text-base font-bold md:row-span-1" />
      <span className="flex min-w-0 flex-col gap-[3px]">
        {/* Two lines on phones: a long Uzbek or Russian title is read in full, not cut after a word. */}
        <span className="line-clamp-2 break-words text-[14.5px] font-semibold leading-snug text-foreground md:line-clamp-1">{result.testTitle}</span>
        <span className="whitespace-nowrap text-[12.5px] text-muted-foreground">{date}</span>
      </span>
      {/* Under the title on phones (column 2, next row); from md a column of its own. */}
      {hasVerdict(result) && (
        <span className="col-start-2 mt-1.5 flex justify-start md:col-start-3 md:mt-0">
          <ResultVerdict result={result} />
        </span>
      )}
    </>
  );

  // The API refuses the review of a running attempt (it would expose the answer
  // key), so an unfinished attempt is listed but never opened.
  if (result.status === 'in_progress') {
    return (
      <div
        className={cn(
          'grid min-h-16 items-center gap-x-3 py-2.5',
          onSelect
            ? 'grid-cols-[56px_minmax(0,1fr)_minmax(92px,auto)_80px] px-5'
            : 'grid-cols-[56px_minmax(0,1fr)_16px] px-4 md:grid-cols-[56px_minmax(0,1fr)_auto_16px]',
        )}
      >
        {body}
        <span aria-hidden="true" className="hidden md:block" />
      </div>
    );
  }

  if (!onSelect) {
    return (
      <Link
        to={`/portal/results/${result.id}`}
        className="grid min-h-16 grid-cols-[56px_minmax(0,1fr)_16px] items-center gap-x-3 px-4 py-2.5 md:grid-cols-[56px_minmax(0,1fr)_auto_16px] transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {body}
        <ChevronRight size={16} className="col-start-3 row-span-2 row-start-1 self-center text-muted-foreground/70 md:col-start-4 md:row-span-1" aria-hidden="true" />
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'grid min-h-16 w-full grid-cols-[56px_minmax(0,1fr)_minmax(92px,auto)_80px] items-center gap-x-3 px-5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        selected ? 'bg-muted' : 'hover:bg-muted/40',
      )}
    >
      {body}
      <span
        className={cn(
          'text-right text-[13px]',
          selected ? 'text-muted-foreground' : 'font-medium text-foreground/80',
        )}
      >
        {selected ? t('result.selected') : t('result.view')}
      </span>
    </button>
  );
}

export default ResultRow;
